import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrganizerAuth } from '../contexts/OrganizerAuthContext';
import { useEvent } from '../contexts/EventContext';
import {
  Lock, Mail, Eye, EyeOff, AlertTriangle,
  QrCode, Users, Search, LogOut, CheckCircle,
  XCircle, Camera, CameraOff, X
} from 'lucide-react';
import CHECKIN_I18N, { SUPPORTED_LANGS, getCheckinLang } from '../i18n/checkinScanner';

const API  = '/api';
const LOGO = 'https://matchmaking.mobivent.fr/logo-2025-Mobivent-400-dark.png';

// ── Helpers ──────────────────────────────────────────────────────────────────

const checkinColor = (status) => {
  if (status === 'Checked In') return 'bg-green-100 text-green-700';
  if (status === 'Cancelled')  return 'bg-red-100 text-red-600';
  return 'bg-gray-600/60 text-gray-300';
};

// ── Lang selector ─────────────────────────────────────────────────────────────

const LangSelector = ({ lang, setLang }) => (
  <div className="flex gap-1">
    {SUPPORTED_LANGS.map(l => (
      <button key={l} onClick={() => { setLang(l); localStorage.setItem('checkin_lang', l); }}
        className={`px-2 py-1 rounded-lg text-xs font-semibold transition ${
          lang === l ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-white hover:bg-white/10'
        }`}>
        {l.toUpperCase()}
      </button>
    ))}
  </div>
);

// ── Login Screen ──────────────────────────────────────────────────────────────

const LoginScreen = ({ event, slug, t, lang, setLang, onSuccess }) => {
  const { login, loading } = useOrganizerAuth();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [error, setError]       = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const subEventId = event?.subevent_id || event?.id || '';
    const result = await login(email, password, subEventId);
    if (result.success) onSuccess();
    else setError(t.login_error);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Lang */}
        <div className="flex justify-end mb-4">
          <LangSelector lang={lang} setLang={setLang} />
        </div>
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={LOGO} alt="Mobivent" className="h-10 mx-auto mb-4"
            onError={e => e.target.style.display = 'none'} />
          <h1 className="text-white text-xl font-bold">{t.login_title}</h1>
          <p className="text-slate-400 text-sm mt-1">{event?.name || slug}</p>
        </div>
        {/* Card */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 shadow-2xl">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5 uppercase tracking-wide">{t.login_email}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  placeholder="you@example.com"
                  className="w-full pl-9 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5 uppercase tracking-wide">{t.login_password}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                  placeholder="••••••••"
                  className="w-full pl-9 pr-10 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm" />
                <button type="button" onClick={() => setShowPwd(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                  {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            {error && (
              <div className="bg-red-500/20 border border-red-500/40 text-red-300 text-sm px-4 py-3 rounded-xl flex items-center gap-2">
                <AlertTriangle size={14} />{error}
              </div>
            )}
            <button type="submit" disabled={loading}
              className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-semibold rounded-xl transition flex items-center justify-center gap-2 text-sm mt-1">
              {loading
                ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />{t.login_loading}</>
                : <><Lock size={15} />{t.login_btn}</>
              }
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// ── Participant Card (fiche avant confirmation) ───────────────────────────────

const ParticipantCard = ({ participant: p, t, onConfirm, onCancel, confirming }) => (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center p-4">
    <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
      {/* Header coloré */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-5">
        <div className="flex items-center gap-4">
          {p.photo
            ? <img src={p.photo} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-white/40 flex-shrink-0" />
            : <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                {(p.name || '?')[0].toUpperCase()}
              </div>
          }
          <div className="min-w-0">
            <p className="text-white font-bold text-lg leading-tight">{p.name}</p>
            {p.job_title && <p className="text-blue-200 text-sm">{p.job_title}</p>}
            {p.company && (
              <span className="inline-block mt-1 bg-white/15 text-white text-xs px-2 py-0.5 rounded-full">{p.company}</span>
            )}
          </div>
        </div>
      </div>
      {/* Statut actuel */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <span className="text-sm text-gray-500">{t.card_current}</span>
        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
          p.checkin_status === 'Checked In' ? 'bg-green-100 text-green-700' :
          p.checkin_status === 'Cancelled'  ? 'bg-red-100 text-red-600' :
          'bg-gray-100 text-gray-500'
        }`}>
          {p.checkin_status === 'Checked In' ? t.status_present :
           p.checkin_status === 'Cancelled'  ? t.status_cancelled : t.status_absent}
        </span>
      </div>
      {/* Contact */}
      {(p.email || p.phone) && (
        <div className="px-5 py-3 border-b border-gray-100 space-y-1">
          {p.email && <p className="text-xs text-gray-500">{p.email}</p>}
          {p.phone && <p className="text-xs text-gray-500">{p.phone}</p>}
        </div>
      )}
      {/* Actions */}
      <div className="p-4 flex gap-3">
        <button onClick={onCancel}
          className="flex-1 py-3 border border-gray-200 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition">
          {t.card_cancel}
        </button>
        <button onClick={onConfirm} disabled={confirming || p.checkin_status === 'Checked In'}
          className={`flex-1 py-3 font-semibold rounded-xl text-sm transition flex items-center justify-center gap-2 ${
            p.checkin_status === 'Checked In'
              ? 'bg-green-100 text-green-700 cursor-default'
              : 'bg-green-600 text-white hover:bg-green-700 disabled:opacity-50'
          }`}>
          {confirming
            ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : p.checkin_status === 'Checked In'
            ? <><CheckCircle size={15} />{t.card_already}</>
            : <><CheckCircle size={15} />{t.card_confirm}</>
          }
        </button>
      </div>
    </div>
  </div>
);

// ── Result Banner ─────────────────────────────────────────────────────────────

const ResultBanner = ({ result, onDismiss }) => {
  useEffect(() => {
    const timer = setTimeout(onDismiss, 3000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div className={`fixed top-4 left-4 right-4 z-50 rounded-2xl p-4 shadow-xl flex items-center gap-3 ${
      result.type === 'success' ? 'bg-green-500' :
      result.type === 'warning' ? 'bg-orange-500' : 'bg-red-500'
    }`}>
      {result.type === 'success' ? <CheckCircle size={22} className="text-white flex-shrink-0" />
        : result.type === 'warning' ? <AlertTriangle size={22} className="text-white flex-shrink-0" />
        : <XCircle size={22} className="text-white flex-shrink-0" />
      }
      <div className="flex-1 min-w-0">
        <p className="text-white font-bold text-sm">{result.title}</p>
        {result.sub && <p className="text-white/80 text-xs mt-0.5">{result.sub}</p>}
      </div>
      <button onClick={onDismiss} className="text-white/70 hover:text-white flex-shrink-0">
        <X size={16} />
      </button>
    </div>
  );
};

// ── Scanner Tab ───────────────────────────────────────────────────────────────

const ScannerTab = ({ orgHeaders, participants, onCheckinUpdate, t }) => {
  const [scanState, setScanState]           = useState('idle'); // idle | scanning | confirming
  const [scannedParticipant, setScannedParticipant] = useState(null);
  const [result, setResult]                 = useState(null);
  const [facingMode, setFacingMode]         = useState('environment');

  const videoRef  = useRef(null);
  const streamRef = useRef(null);
  const rafRef    = useRef(null);
  const canvasRef = useRef(document.createElement('canvas'));
  const scanning  = useRef(false);

  const stopCamera = useCallback(() => {
    scanning.current = false;
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const processFrame = useCallback(() => {
    if (!scanning.current) return;
    const video  = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || video.readyState < 2) {
      rafRef.current = requestAnimationFrame(processFrame);
      return;
    }
    canvas.width  = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const imageData = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);

    import('jsqr').then(({ default: jsQR }) => {
      if (!scanning.current) return;
      const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: 'dontInvert' });
      if (code) {
        scanning.current = false;
        stopCamera();
        handleQrDetected(code.data);
      } else {
        rafRef.current = requestAnimationFrame(processFrame);
      }
    });
  }, []); // eslint-disable-line

  const handleQrDetected = useCallback((rawData) => {
    let recordId = '';

    // Format URL : .../participants-networking/{recordId}
    const urlMatch = rawData.match(/\/participants-networking\/([^/?#]+)/);
    if (urlMatch) {
      recordId = decodeURIComponent(urlMatch[1]);
    } else {
      // Rétrocompat : ancien format JSON { id: "recXXX", ... }
      try {
        const qrData = JSON.parse(rawData);
        recordId = qrData.id || '';
      } catch {
        setResult({ type: 'error', title: t.result_bad_format, sub: t.result_bad_sub });
        setScanState('idle');
        return;
      }
    }

    if (!recordId) {
      setResult({ type: 'error', title: t.result_invalid_qr, sub: t.result_invalid_sub });
      setScanState('idle');
      return;
    }

    const found = participants.find(p => p.id === recordId);
    if (found) {
      setScannedParticipant(found);
      setScanState('preview');
    } else {
      setScannedParticipant({
        id: recordId,
        name: recordId,
        company: '',
        job_title: '',
        email: '',
        phone: '',
        photo: null,
        checkin_status: '',
      });
      setScanState('preview');
    }
  }, [participants, t]);

  const startCamera = useCallback(async () => {
    stopCamera();
    scanning.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      rafRef.current = requestAnimationFrame(processFrame);
    } catch {
      scanning.current = false;
      setScanState('idle');
    }
  }, [facingMode, stopCamera, processFrame]);

  const handleConfirm = async () => {
    if (!scannedParticipant) return;
    setScanState('confirming');
    try {
      const res  = await fetch(`${API}/checkin/${scannedParticipant.id}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json', ...orgHeaders() },
        body:    JSON.stringify({ status: 'Checked In' }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        onCheckinUpdate(scannedParticipant.id, 'Checked In');
        setResult({ type: 'success', title: t.result_success, sub: scannedParticipant.name });
      } else {
        setResult({ type: 'error', title: t.result_error, sub: data.error || '' });
      }
    } catch {
      setResult({ type: 'error', title: t.result_network, sub: t.result_network_sub });
    } finally {
      setScannedParticipant(null);
      setScanState('idle');
    }
  };

  useEffect(() => {
    if (scanState === 'scanning') startCamera();
    return () => { if (scanState !== 'scanning') stopCamera(); };
  }, [scanState]); // eslint-disable-line

  useEffect(() => () => stopCamera(), [stopCamera]);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-10">
      {result && <ResultBanner result={result} onDismiss={() => setResult(null)} />}

      {(scanState === 'preview' || scanState === 'confirming') && scannedParticipant && (
        <ParticipantCard
          participant={scannedParticipant}
          t={t}
          confirming={scanState === 'confirming'}
          onConfirm={handleConfirm}
          onCancel={() => { setScannedParticipant(null); setScanState('idle'); }}
        />
      )}

      {scanState === 'scanning' ? (
        <div className="w-full max-w-sm">
          <div className="relative rounded-2xl overflow-hidden bg-black aspect-square shadow-2xl">
            <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
            {/* Cadre */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-56 h-56 relative">
                <div className="absolute top-0 left-0   w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-lg" />
                <div className="absolute top-0 right-0  w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-lg" />
                <div className="absolute bottom-0 left-0  w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-lg" />
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-lg" />
              </div>
            </div>
            <p className="absolute bottom-4 left-0 right-0 text-center text-white/70 text-xs pointer-events-none">
              {t.scan_aim}
            </p>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => { stopCamera(); setFacingMode(m => m === 'environment' ? 'user' : 'environment'); setScanState('scanning'); }}
              className="flex-1 py-3 bg-white/10 backdrop-blur border border-white/20 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2 hover:bg-white/20 transition">
              <Camera size={16} />{t.scan_flip}
            </button>
            <button onClick={() => { stopCamera(); setScanState('idle'); }}
              className="flex-1 py-3 bg-red-500/80 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2 hover:bg-red-500 transition">
              <CameraOff size={16} />{t.scan_stop}
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <div className="w-24 h-24 rounded-3xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center mx-auto mb-6 shadow-lg">
            <QrCode size={44} className="text-white" />
          </div>
          <h2 className="text-white text-xl font-bold mb-2">{t.scan_title}</h2>
          <p className="text-slate-400 text-sm mb-8 max-w-xs mx-auto">{t.scan_subtitle}</p>
          <button onClick={() => setScanState('scanning')}
            className="px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-2xl shadow-lg transition flex items-center gap-3 mx-auto text-base">
            <Camera size={20} />{t.scan_btn}
          </button>
        </div>
      )}
    </div>
  );
};

// ── List Tab ──────────────────────────────────────────────────────────────────

const ListTab = ({ participants: initialParticipants, orgHeaders, onCheckinUpdate, t }) => {
  const [participants, setParticipants] = useState(initialParticipants);
  const [search, setSearch]             = useState('');
  const [loadingId, setLoadingId]       = useState(null);

  useEffect(() => { setParticipants(initialParticipants); }, [initialParticipants]);

  const filtered = participants.filter(p =>
    !search || `${p.name} ${p.company}`.toLowerCase().includes(search.toLowerCase())
  );

  const handleCheckin = async (p) => {
    const newStatus = p.checkin_status === 'Checked In' ? 'Not Checked In' : 'Checked In';
    setLoadingId(p.id);
    try {
      const res = await fetch(`${API}/checkin/${p.id}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json', ...orgHeaders() },
        body:    JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setParticipants(prev => prev.map(x => x.id === p.id ? { ...x, checkin_status: newStatus } : x));
        onCheckinUpdate(p.id, newStatus);
      }
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="px-4 py-3 bg-white/5 border-b border-white/10">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
          <input type="text" placeholder={t.list_search} value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm" />
        </div>
        <p className="text-slate-500 text-xs mt-1.5">
          {filtered.filter(p => p.checkin_status === 'Checked In').length} / {filtered.length} {t.list_present}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filtered.map(p => (
          <div key={p.id} className="flex items-center gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/5 transition">
            {p.photo
              ? <img src={p.photo} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
              : <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {(p.name || '?')[0].toUpperCase()}
                </div>
            }
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm truncate">{p.name}</p>
              <p className="text-slate-400 text-xs truncate">{p.company || p.job_title || '—'}</p>
            </div>
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${checkinColor(p.checkin_status)}`}>
                {p.checkin_status === 'Checked In' ? t.status_present :
                 p.checkin_status === 'Cancelled'  ? t.status_cancelled : t.status_absent}
              </span>
              <button onClick={() => handleCheckin(p)}
                disabled={loadingId === p.id || p.checkin_status === 'Cancelled'}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition disabled:opacity-40 ${
                  p.checkin_status === 'Checked In'
                    ? 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                    : 'bg-green-600 text-white hover:bg-green-500'
                }`}>
                {loadingId === p.id ? '…' : p.checkin_status === 'Checked In' ? t.list_undo_btn : t.list_checkin_btn}
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="text-center text-slate-500 py-12 text-sm">{t.list_empty}</p>
        )}
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const CheckinScannerPage = () => {
  const { slug }   = useParams();
  const navigate   = useNavigate();
  const { organizer, logout, orgHeaders } = useOrganizerAuth();
  const { event, loadEventBySlug }        = useEvent();

  const [lang, setLang]                 = useState(getCheckinLang);
  const [tab, setTab]                   = useState('scan');
  const [participants, setParticipants] = useState([]);
  const [loadingData, setLoadingData]   = useState(false);

  const t = CHECKIN_I18N[lang] || CHECKIN_I18N.fr;

  useEffect(() => {
    if (slug && (!event || event.slug !== slug)) loadEventBySlug(slug);
  }, [slug]); // eslint-disable-line

  const loadParticipants = useCallback(async () => {
    const subEventId = event?.subevent_id || event?.id;
    if (!subEventId) return;
    setLoadingData(true);
    try {
      const res  = await fetch(`${API}/participants/${subEventId}`, { headers: orgHeaders() });
      const data = await res.json();
      if (data.success) setParticipants(data.data || []);
    } finally {
      setLoadingData(false);
    }
  }, [event, orgHeaders]);

  useEffect(() => { if (organizer && event) loadParticipants(); }, [organizer, event]); // eslint-disable-line

  const handleCheckinUpdate = (recordId, newStatus) => {
    setParticipants(prev => prev.map(p => p.id === recordId ? { ...p, checkin_status: newStatus } : p));
  };

  const handleLogout = () => {
    logout();
    navigate(slug ? `/event/${slug}/checkin` : '/checkin');
  };

  if (!organizer) {
    return <LoginScreen event={event} slug={slug} t={t} lang={lang} setLang={setLang} onSuccess={loadParticipants} />;
  }

  const checkedInCount = participants.filter(p => p.checkin_status === 'Checked In').length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-blue-950 to-slate-900 flex flex-col">

      {/* Header */}
      <header className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-white/10 bg-black/20 backdrop-blur">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="Mobivent" className="h-7"
            onError={e => e.target.style.display = 'none'} />
          <div className="hidden sm:block">
            <p className="text-white text-sm font-semibold leading-tight">{event?.name || slug}</p>
            <p className="text-slate-400 text-xs">{t.header_checkin}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LangSelector lang={lang} setLang={setLang} />
          <div className="text-right">
            <p className="text-white text-sm font-bold">
              {checkedInCount} <span className="text-slate-400 font-normal">/ {participants.length}</span>
            </p>
            <p className="text-slate-500 text-xs">{t.header_present}</p>
          </div>
          <button onClick={handleLogout}
            className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-xl transition">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex-shrink-0 flex border-b border-white/10 bg-black/10">
        <button onClick={() => setTab('scan')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition border-b-2 ${
            tab === 'scan' ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-300'
          }`}>
          <QrCode size={16} />{t.tab_scan}
        </button>
        <button onClick={() => { setTab('list'); loadParticipants(); }}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition border-b-2 ${
            tab === 'list' ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-300'
          }`}>
          <Users size={16} />{t.tab_list}
          {participants.length > 0 && (
            <span className="bg-white/10 text-slate-300 text-xs px-1.5 py-0.5 rounded-full">{participants.length}</span>
          )}
        </button>
      </div>

      {/* Content */}
      {loadingData && participants.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-slate-400">
            <div className="w-8 h-8 border-2 border-slate-600 border-t-blue-400 rounded-full animate-spin" />
            <span className="text-sm">{t.loading}</span>
          </div>
        </div>
      ) : (
        <>
          {tab === 'scan' && (
            <ScannerTab participants={participants} orgHeaders={orgHeaders} onCheckinUpdate={handleCheckinUpdate} t={t} />
          )}
          {tab === 'list' && (
            <ListTab participants={participants} orgHeaders={orgHeaders} onCheckinUpdate={handleCheckinUpdate} t={t} />
          )}
        </>
      )}
    </div>
  );
};

export default CheckinScannerPage;
